"use client";

import {
  useRouter as useNextRouter,
  usePathname,
  useSearchParams
} from "next/navigation";
import { useMemo } from "react";

type QueryValue = string | string[];
type Query = Record<string, QueryValue>;

type NavigateOptions = {
  scroll?: boolean;
  shallow?: boolean; // compat (ignored in App Router)
};

type UrlObject = {
  pathname?: string;
  query?: Record<string, string | string[] | undefined>;
  hash?: string;
};

type Url = string | UrlObject;

type LegacyRouter = {
  push: (url: Url, as?: string, options?: NavigateOptions) => void;
  replace: (url: Url, as?: string, options?: NavigateOptions) => void;
  prefetch: (url: string) => Promise<void>;
  back: () => void;
  forward: () => void;
  reload: () => void;
  pathname: string;
  asPath: string;
  query: Query;
  events: { on: (..._args: any[]) => void; off: (..._args: any[]) => void };
  isReady: boolean;
};

function buildQuery(params: URLSearchParams): Query {
  const q: Query = {};
  params.forEach((value, key) => {
    if (q[key] === undefined) {
      const all = params.getAll(key);
      q[key] = all.length > 1 ? all : value;
    } else if (Array.isArray(q[key])) {
      (q[key] as string[]).push(value);
    } else {
      q[key] = [q[key] as string, value];
    }
  });
  return q;
}

function stableSearchKeyFromSearch(search: string): string {
  const params = new URLSearchParams(search);
  const keys = Array.from(new Set(Array.from(params.keys()))).sort();

  const parts: string[] = [];
  for (const k of keys) {
    const values = params.getAll(k);
    for (const v of values) parts.push(`${k}=${v}`);
  }
  return parts.join("&");
}

function toSearchParams(
  query?: Record<string, string | string[] | undefined>
): URLSearchParams {
  const sp = new URLSearchParams();
  if (!query) return sp;

  for (const [key, value] of Object.entries(query)) {
    if (Array.isArray(value)) {
      for (const v of value) sp.append(key, String(v));
    } else if (value !== undefined) {
      sp.set(key, String(value));
    }
  }

  return sp;
}

function normalizeHash(hash?: string): string {
  if (!hash) return "";
  return hash.startsWith("#") ? hash : `#${hash}`;
}

function toUrl(url: Url, currentPathname: string): string {
  if (typeof url === "string") return url;

  const pathname = url.pathname ?? currentPathname;
  const sp = toSearchParams(url.query);
  const qs = sp.toString();
  const hash = normalizeHash(url.hash);

  return `${pathname}${qs ? `?${qs}` : ""}${hash}`;
}

export const useRouter = (): LegacyRouter => {
  const router = useNextRouter();
  const pathname = usePathname() || "";
  const searchParams = useSearchParams();

  const search = searchParams?.toString() || "";
  const asPath = search ? `${pathname}?${search}` : pathname;

  const queryKey = useMemo(() => stableSearchKeyFromSearch(search), [search]);

  const query = useMemo(
    () => buildQuery(new URLSearchParams(search)),
    [queryKey]
  );

  return {
    push: (url, _as, options) =>
      router.push(toUrl(url, pathname), { scroll: options?.scroll }),

    replace: (url, _as, options) =>
      router.replace(toUrl(url, pathname), { scroll: options?.scroll }),

    prefetch: async (_url: string) => Promise.resolve(),
    back: () => router.back(),
    forward: () => {
      if (typeof window !== "undefined") window.history.forward();
    },
    reload: () => router.refresh(),

    pathname,
    asPath,
    query,

    events: { on: () => {}, off: () => {} },
    isReady: true
  };
};
